<template>
  <div ref="root" class="rd rd--posting" :class="{ 'ui-compact': compact }" :data-w="layout" :data-density="density" :data-bar="barSticky ? 'sticky' : 'flow'" :style="rootStyle">
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('finance_posting.title')" @select="openScreen">
        <template #tools>
          <button type="button" class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain" :title="`${$t('report_desktop.shortcuts')} (?)`" :aria-label="$t('report_desktop.shortcuts')" :aria-expanded="String(helpOpen)" @click="helpOpen = !helpOpen">
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
        </template>
      </report-nav>

      <finance-period-bar :from="period.from" :to="period.to" :presets="presets" :compact="compact" @period="setPeriod" />

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ $t("finance_posting.title") }}</h1>
            <p class="rd-head__sub">{{ $t("finance_posting.subtitle") }}</p>
          </div>
          <div class="rd-head__right">
            <button type="button" class="ui-icon-btn ui-icon-btn--sm" :title="`${$t('refresh')} (r)`" :aria-label="$t('refresh')" @click="reload">
              <v-icon size="18">mdi-refresh</v-icon>
            </button>
          </div>
        </div>
      </div>

      <ui-filter-bar v-if="status === 'ready' && allRows.length" ref="bar" :segments="segments" :query="query" :placeholder="$t('finance_posting.search')" :search-label="$t('search')" :count="$t('finance_posting.count', { shown: rows.length, total: allRows.length })" @segment="setSegment" @query="query = $event" />

      <div class="rd-page ui-screen">
        <ui-state v-if="status === 'loading'" kind="loading" />
        <ui-state v-else-if="status === 'error'" kind="error" icon="mdi-alert-outline" :title="$t('finance_posting.error_title')" :text="$t('finance_posting.error_text')">
          <ui-button variant="dark" icon="mdi-refresh" @click="reload">{{ $t("finance_posting.retry") }}</ui-button>
        </ui-state>
        <ui-state v-else-if="!allRows.length" kind="empty" icon="mdi-check-circle-outline" :title="$t('finance_posting.empty_title')" :text="$t('finance_posting.empty_text')" />
        <template v-else>
          <div class="ui-kpis" role="group" :aria-label="$t('finance_posting.title')">
            <div class="ui-kpi"><small>{{ $t("finance_posting.kpi_count") }}</small><b class="ui-num">{{ meta.total }}</b></div>
            <div class="ui-kpi"><small>{{ $t("finance_posting.kpi_sum") }}</small><b class="ui-num">{{ money(meta.valueSum) }}</b></div>
            <div class="ui-kpi"><small>{{ $t("finance_posting.kpi_oldest") }}</small><b class="ui-num">{{ oldest ? localeDate(oldest) : "—" }}</b></div>
          </div>

          <ui-banner v-if="meta.truncated" tone="warn">{{ $t("finance_posting.truncated", { returned: allRows.length, total: meta.total }) }}</ui-banner>

          <div v-if="selected.length" class="pk-bulk" role="region" :aria-label="$t('finance_posting.post_selected')">
            <ui-confirm v-if="confirmBulk" :title="$t('finance_posting.confirm_title')">
              {{ $t("finance_posting.confirm_text", { count: selected.length, sum: money(selectedSum) }) }}
              <template #actions>
                <ui-button size="sm" :disabled="bulkBusy" @click="confirmBulk = false">{{ $t("cancel") }}</ui-button>
                <ui-button size="sm" variant="primary" icon="mdi-check-all" :disabled="bulkBusy" @click="postSelected">{{ $t("finance_posting.post") }}</ui-button>
              </template>
            </ui-confirm>
            <template v-else>
              <span class="pk-bulk__text">{{ $t("finance_posting.selected", { count: selected.length, max: maxBulk }) }} · <b class="ui-num">{{ money(selectedSum) }}</b></span>
              <ui-button size="sm" variant="primary" icon="mdi-check-all" :disabled="bulkBusy" @click="confirmBulk = true">{{ $t("finance_posting.post_selected") }}</ui-button>
              <button type="button" class="ui-text-btn" @click="clearSelection">{{ $t("finance_posting.clear_selection") }}</button>
            </template>
          </div>

          <ui-state v-if="!rows.length" kind="empty" icon="mdi-magnify" :title="$t('finance_posting.filtered_title')" :text="$t('finance_posting.filtered_text')">
            <ui-button @click="resetFilters">{{ $t("finance_posting.reset") }}</ui-button>
          </ui-state>
          <ui-table v-else :columns="columns" :rows="rows" :row-key="(row) => row.key" :row-class="rowClass" :end-label="$t('actions')" @open="openRow">
            <template #head-select>
              <input type="checkbox" class="pk-check" :checked="allSelected" :aria-label="$t('finance_posting.select_all')" @change="toggleAll" />
            </template>
            <template #cell-select="{ row }">
              <input type="checkbox" class="pk-check" :checked="isSelected(row)" :disabled="!isSelected(row) && selectionFull" :aria-label="$t('finance_posting.col_select')" @click.stop @change="toggle(row)" />
            </template>
            <template #cell-order="{ row }">
              <b class="nw">#{{ row.orderId }}</b>
              <small v-if="results[row.key]" class="pk-res" :class="`pk-res--${resultTone(results[row.key].status)}`">{{ resultLine(results[row.key]) }}</small>
            </template>
            <template #cell-date="{ row }">{{ row.date ? localeDate(row.date) : "—" }}</template>
            <template #cell-restaurant="{ row }">
              <b>{{ restaurantText(row) }}</b>
              <small v-if="row.restaurantId !== null">#{{ row.restaurantId }}</small>
            </template>
            <template #cell-outcome="{ row }">
              <ui-chip v-if="row.outcome" :tone="outcomeTone(row.outcome)" dot>{{ outcomeText(row.outcome) }}</ui-chip>
              <span v-else class="ui-muted">—</span>
            </template>
            <template #cell-payment="{ row }">{{ paymentText(row.paymentMethod) || "—" }}</template>
            <template #cell-courier="{ row }">{{ row.courier ? toLatin(row.courier) : "—" }}</template>
            <template #cell-value="{ row }">{{ row.value === null ? "—" : money(row.value, row.restaurantId) }}</template>
            <template #cell-waiting="{ row }">{{ row.finalAt ? $t("finance_shell.pending.since", { date: localeDate(row.finalAt) }) : "—" }}</template>
            <template #end="{ row }">
              <ui-button size="sm" variant="primary" :disabled="Boolean(busy[row.key]) || bulkBusy" @click.stop="confirmPost(row)">{{ $t("finance_posting.post") }}</ui-button>
            </template>

            <template #card="{ row }">
              <ui-item :tone="row.outcome === 'failed' ? 'warn' : ''" :active="row.key === activeKey" @click="openRow(row)">
                <span class="ui-who">
                  <span>
                    <b>#{{ row.orderId }}</b>
                    <small>{{ restaurantText(row) }}<template v-if="row.restaurantId !== null"> · #{{ row.restaurantId }}</template></small>
                  </span>
                </span>
                <span v-if="row.outcome" class="ui-item__tags"><ui-chip :tone="outcomeTone(row.outcome)" dot>{{ outcomeText(row.outcome) }}</ui-chip></span>
                <span class="ui-item__line">
                  <span v-if="row.date" class="ui-num">{{ localeDate(row.date) }}</span>
                  <span v-if="paymentText(row.paymentMethod)">{{ paymentText(row.paymentMethod) }}</span>
                  <span v-if="row.courier">{{ toLatin(row.courier) }}</span>
                  <b v-if="row.value !== null" class="ui-num">{{ money(row.value, row.restaurantId) }}</b>
                </span>
                <span v-if="row.finalAt" class="ui-item__line"><span>{{ $t("finance_shell.pending.since", { date: localeDate(row.finalAt) }) }}</span></span>
                <span v-if="results[row.key]" class="pk-res" :class="`pk-res--${resultTone(results[row.key].status)}`">{{ resultLine(results[row.key]) }}</span>
              </ui-item>
              <div class="ui-item-actions">
                <label class="pk-pick">
                  <input type="checkbox" class="pk-check" :checked="isSelected(row)" :disabled="!isSelected(row) && selectionFull" @change="toggle(row)" />
                  {{ $t("finance_posting.col_select") }}
                </label>
                <ui-button size="sm" variant="primary" :disabled="Boolean(busy[row.key]) || bulkBusy" @click="confirmPost(row)">{{ $t("finance_posting.post") }}</ui-button>
              </div>
            </template>
          </ui-table>

          <p class="pk-note ui-muted">{{ $t("finance_posting.note") }}</p>
        </template>
      </div>
    </div>

    <report-detail-panel :stack="panelStack" @close="closePanel" @back="panelBack" @open="openPanel" @toast="onPanelToast" />
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false" />
    <global-confirm-box ref="confirmBox" />
    <ui-toast :toast="toast" @action="runToastAction" />
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import "@/components/finance-shell/financeShell.scss";
import "./postingShell.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import FinancePeriodBar from "@/components/finance-shell/parts/FinancePeriodBar.vue";
import { FINANCE_QUERY, buildFinancePresets, parseRange } from "@/components/finance-shell/financeQuery";
import { formatMoney } from "@/components/restaurant/finance/constants";
import ledgerAdminService, { MAX_BULK_POST } from "@/services/ledgerAdmin";
import OrderCheckPanel from "./panels/OrderCheckPanel.vue";
import { countResults, filterMissing, mapBulkResults, mapMissingList, mapPostResult, oldestFinalAt, sortMissing, sumValue } from "./postingLogic";

const COMPACT_WIDTH = 760;
const DEFAULT_PERIOD = "last_30";
const RESULT_TONES = { posted: "ok", skipped: "warn", no_op: "plain", failed: "bad" };

/**
 * Knjiženje narudžbi (/ledger-posting, samo admin): završene narudžbe svih restorana koje još nisu proknjižene (noćna provjera ih
 * knjiži sama; ovdje se mogu proknjižiti odmah). Period (zadano zadnjih 30 dana), filteri ishoda i plaćanja, pretraga, jedna narudžba
 * ("Proknjiži" u redu ili u panelu sa stanjem i knjiženjima) ili više njih ("Proknjiži označene", najviše 200, uz potvrdu).
 * Ishod po narudžbi (posted, skipped, no_op, failed) se piše uz red; poslije knjiženja se lista ponovo učitava sa servera.
 * Dupli klik je bezbjedan: dugme je onemogućeno dok zahtjev traje, a backend ne knjiži narudžbu dvaput.
 */
export default {
  name: "PostingShell",
  mixins: [hasReportShell],
  components: { ReportNav, ReportHelpPopover, ReportDetailPanel, FinancePeriodBar },
  data: () => ({
    status: "loading",
    list: [],
    meta: { total: 0, returned: 0, truncated: false, valueSum: 0 },
    query: "",
    outcome: "all",
    payment: "all",
    selected: [],
    results: {},
    busy: {},
    bulkBusy: false,
    confirmBulk: false,
    confirming: false,
    maxBulk: MAX_BULK_POST,
  }),
  computed: {
    phone() {
      return Boolean(this.device && this.device.isMobile);
    },
    compact() {
      return this.phone || this.width < COMPACT_WIDTH;
    },
    presets() {
      return buildFinancePresets();
    },
    defaultPeriod() {
      return this.presets.find((preset) => preset.key === DEFAULT_PERIOD);
    },
    period() {
      const range = parseRange(this.$route.query[FINANCE_QUERY.from], this.$route.query[FINANCE_QUERY.to]);
      return range ? { from: range[0], to: range[1] } : { from: this.defaultPeriod.from, to: this.defaultPeriod.to };
    },
    periodKey() {
      return `${this.period.from}|${this.period.to}`;
    },
    menuKeys() {
      return ["missing"];
    },
    currentKey() {
      return "missing";
    },
    menuGroups() {
      return [{ key: "posting", title: this.$t("finance_posting.title"), items: [{ key: "missing", icon: "mdi-book-check-outline", title: this.$t("finance_posting.title"), badge: this.meta.total || "" }] }];
    },
    allRows() {
      return sortMissing(this.list);
    },
    /** prikazani redovi: ishod, plaćanje i pretraga (broj narudžbe, restoran, kurir; latinica i ćirilica) */
    rows() {
      const filtered = filterMissing(this.allRows, { outcome: this.outcome, payment: this.payment });
      const fields = ["orderId", "restaurantName", "restaurantId", "courier"].map((value) => ({ value }));
      return this.customSearch(filtered, fields, this.query);
    },
    columns() {
      return [
        { key: "select", label: this.$t("finance_posting.col_select"), sr: true, cls: "pk-col-check" },
        { key: "order", label: this.$t("finance_posting.col_order") },
        { key: "date", label: this.$t("date"), hide: "sm" },
        { key: "restaurant", label: this.$t("restaurant") },
        { key: "outcome", label: this.$t("finance_posting.col_outcome") },
        { key: "payment", label: this.$t("finance_posting.col_payment"), hide: "sm" },
        { key: "courier", label: this.$t("finance_posting.col_courier"), hide: "md" },
        { key: "value", label: this.$t("finance_posting.col_value"), align: "end", num: true },
        { key: "waiting", label: this.$t("finance_posting.col_waiting"), hide: "sm" },
      ];
    },
    segments() {
      return [
        {
          id: "outcome",
          label: this.$t("finance_posting.col_outcome"),
          value: this.outcome,
          options: [
            { value: "all", label: this.$t("finance_posting.all_outcomes") },
            { value: "delivered", label: this.outcomeText("delivered") },
            { value: "failed", label: this.outcomeText("failed") },
          ],
        },
        {
          id: "payment",
          label: this.$t("finance_posting.col_payment"),
          value: this.payment,
          options: [
            { value: "all", label: this.$t("finance_posting.all_payments") },
            { value: "CASH", label: this.$t("finance_payment_cash") },
            { value: "CARD", label: this.$t("finance_payment_card") },
          ],
        },
      ];
    },
    oldest() {
      return oldestFinalAt(this.allRows);
    },
    selectedRows() {
      return this.allRows.filter((row) => this.selected.includes(row.key));
    },
    selectedSum() {
      return sumValue(this.selectedRows);
    },
    selectionFull() {
      return this.selected.length >= MAX_BULK_POST;
    },
    allSelected() {
      const first = this.rows.slice(0, MAX_BULK_POST);
      return first.length > 0 && first.every((row) => this.selected.includes(row.key));
    },
    activeKey() {
      const top = this.panelStack[this.panelStack.length - 1];
      return top && top.props && top.props.row ? top.props.row.key : "";
    },
    shortcuts() {
      return [
        { keys: "r", label: this.$t("finance_shell.keys.refresh") },
        { keys: "/", label: this.$t("finance_shell.keys.search") },
        { keys: "Esc", label: this.$t("report_desktop.keys_close") },
        { keys: "?", label: this.$t("report_desktop.shortcuts") },
      ];
    },
  },
  watch: {
    periodKey() {
      this.selected = [];
      this.results = {};
      this.closePanel();
      this.loadList();
    },
  },
  created() {
    this.loadList();
  },
  methods: {
    // hasReportShell zove ove kad korisnik pritisne r, [ ili ] i broj
    reload() {
      this.loadList();
    },
    shiftDay() {
      // lista nema dane
    },
    openScreen() {
      // jedan ekran
    },
    // ------------------------------------------------------------ podaci
    money(value, restaurantId) {
      return formatMoney(value, (restaurantId !== undefined && this.getCurrencyByRestaurantId(restaurantId)) || "KM");
    },
    async loadList({ silent = false } = {}) {
      this.seq = (this.seq || 0) + 1;
      const seq = this.seq;
      if (!silent) this.status = "loading";
      try {
        const { data } = await ledgerAdminService.getMissing({ from: this.period.from, to: this.period.to, limit: MAX_BULK_POST });
        if (seq !== this.seq) return;
        const list = mapMissingList(data);
        this.list = list.rows;
        this.meta = { total: list.total, returned: list.returned, truncated: list.truncated, valueSum: list.valueSum };
        // izbor i ishodi samo za narudžbe koje su ostale na listi
        const keys = list.rows.map((row) => row.key);
        this.selected = this.selected.filter((key) => keys.includes(key));
        this.status = "ready";
      } catch (error) {
        if (seq !== this.seq) return;
        if (silent) this.showToast(this.$t("finance_posting.error_title"));
        else this.status = "error";
      }
    },
    // ------------------------------------------------------------ filteri
    setPeriod({ from, to }) {
      const isDefault = from === this.defaultPeriod.from && to === this.defaultPeriod.to;
      const query = { ...this.$route.query, [FINANCE_QUERY.from]: isDefault ? undefined : from, [FINANCE_QUERY.to]: isDefault ? undefined : to };
      Object.keys(query).forEach((key) => query[key] === undefined && delete query[key]);
      this.$router.replace({ query }).catch(() => {});
    },
    setSegment(id, value) {
      this[id] = value;
    },
    resetFilters() {
      this.query = "";
      this.outcome = "all";
      this.payment = "all";
    },
    // ------------------------------------------------------------ izbor
    isSelected(row) {
      return this.selected.includes(row.key);
    },
    toggle(row) {
      this.confirmBulk = false;
      if (this.isSelected(row)) this.selected = this.selected.filter((key) => key !== row.key);
      else if (!this.selectionFull) this.selected = [...this.selected, row.key];
    },
    /** označi prikazane redove (najviše 200) ili skini izbor kad su svi označeni */
    toggleAll() {
      this.confirmBulk = false;
      if (this.allSelected) {
        const shown = this.rows.map((row) => row.key);
        this.selected = this.selected.filter((key) => !shown.includes(key));
        return;
      }
      const next = [...this.selected];
      this.rows.forEach((row) => {
        if (next.length < MAX_BULK_POST && !next.includes(row.key)) next.push(row.key);
      });
      this.selected = next;
    },
    clearSelection() {
      this.selected = [];
      this.confirmBulk = false;
    },
    // ------------------------------------------------------------ knjiženje
    errorMessage(error) {
      const response = error && error.response && error.response.data;
      const message = response && typeof response.message === "string" ? response.message : "";
      return message ? this.translate(message) : this.$t("finance_posting.post_error");
    },
    resultTone(status) {
      return RESULT_TONES[status] || "plain";
    },
    statusText(status) {
      const key = `finance_posting.st_${status}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    resultLine(result) {
      return result.message ? `${this.statusText(result.status)}: ${this.translate(result.message)}` : this.statusText(result.status);
    },
    /** tekst potvrde (GlobalConfirmBox ga prikazuje kao HTML, pa se podaci sa servera escape-uju) */
    confirmOneText(row) {
      const esc = (text) => String(text).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);
      const value = row.value === null ? "" : ` · ${this.money(row.value, row.restaurantId)}`;
      return `<b>${esc(this.restaurantText(row))}</b>${esc(value)}<br>${esc(this.$t("finance_posting.confirm_one_text"))}`;
    },
    /** Proknjiži jednu narudžbu (red ili panel) tek poslije potvrde; vraća ishod, a null kad se odustane */
    async confirmPost(row) {
      if (this.confirming || this.busy[row.key] || this.bulkBusy) return null;
      this.confirming = true;
      try {
        await this.$refs.confirmBox.open(this.$t("finance_posting.confirm_one_title", { id: row.orderId }), this.confirmOneText(row), { color: "warning" });
      } catch (error) {
        return null;
      } finally {
        this.confirming = false;
      }
      return this.postOne(row);
    },
    /** Proknjiži jednu narudžbu (poziva se samo poslije potvrde); vraća ishod, a lista se ponovo učitava */
    async postOne(row) {
      if (this.busy[row.key] || this.bulkBusy) return null;
      this.$set(this.busy, row.key, true);
      let result;
      try {
        const { data } = await ledgerAdminService.postOrder(row.orderId);
        result = mapPostResult(data, row.orderId);
      } catch (error) {
        result = { orderId: row.orderId, status: "failed", transactionId: null, message: this.errorMessage(error) };
      }
      this.$delete(this.busy, row.key);
      this.$set(this.results, row.key, result);
      this.selected = this.selected.filter((key) => key !== row.key);
      this.showToast(result.status === "posted" ? this.$t("finance_posting.single_posted", { id: row.orderId }) : this.resultLine(result));
      await this.loadList({ silent: true });
      return result;
    },
    async postSelected() {
      const rows = this.selectedRows;
      if (!rows.length || rows.length > MAX_BULK_POST || this.bulkBusy) return;
      this.bulkBusy = true;
      const ids = rows.map((row) => row.orderId);
      let results;
      try {
        const { data } = await ledgerAdminService.postOrders(ids);
        results = mapBulkResults(data, ids);
      } catch (error) {
        this.showToast(this.errorMessage(error));
        this.bulkBusy = false;
        this.confirmBulk = false;
        return;
      }
      results.forEach((result) => this.$set(this.results, String(result.orderId), result));
      const counts = countResults(results);
      this.showToast(this.$t("finance_posting.summary", { posted: counts.posted, skipped: counts.skipped, no_op: counts.no_op, failed: counts.failed }), { duration: 6000 });
      this.selected = [];
      this.confirmBulk = false;
      await this.loadList({ silent: true });
      this.bulkBusy = false;
    },
    // ------------------------------------------------------------ prikaz
    rowClass(row) {
      return { "is-on": row.key === this.activeKey, "pk-row--failed": row.outcome === "failed" };
    },
    restaurantText(row) {
      return row.restaurantName ? this.toLatin(row.restaurantName) : row.restaurantId !== null ? `#${row.restaurantId}` : "—";
    },
    outcomeText(outcome) {
      const key = `finance_shell.pending.outcome.${outcome}`;
      return this.$te(key) ? this.$t(key) : this.toLatin(outcome);
    },
    outcomeTone(outcome) {
      return outcome === "delivered" ? "ok" : outcome === "failed" ? "warn" : "plain";
    },
    paymentText(method) {
      return method === "CARD" ? this.$t("finance_payment_card") : method === "CASH" ? this.$t("finance_payment_cash") : "";
    },
    openRow(row) {
      this.openPanel({
        key: `order-${row.orderId}`,
        title: this.$t("finance_posting.panel_title", { id: row.orderId }),
        subtitle: this.restaurantText(row),
        component: OrderCheckPanel,
        props: { row },
        root: true,
      });
    },
    onPanelToast(text, options) {
      this.showToast(text, options || {});
    },
    onKeydown(event) {
      hasReportShell.methods.onKeydown.call(this, event);
    },
  },
};
</script>
